<script>
    import { onMount } from "svelte";

    import Select from "svelte-select/Select.svelte";

    import {
        Button,
        CardLayout,
        Loader,
        PageHead,
        RichTextEditor,
    } from "../../Components/Common/index.svelte";

    import {
        allGroups,
        loggedInDoctorDetail,
        prePopulateAccountOptions,
        user,
    } from "../../stores";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import {
        goBack,
        goToURL,
        isObjectEmpty,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import {
        cacheAccountPrePopulateSearchOptions,
        cacheAllGroups,
        cachePreOpInstructions,
    } from "../../Utils/utilCache.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    $: submitBtnDisabled = _checkSubmitBtnDisabled(preOpInstruction);

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;
    $: isUserSupport = $user.role == DirectUserRoles.Support;
    $: isUserManager = $user.role == DirectUserRoles.Manager;
    $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

    $: isUserEnableSelectGroup = isUserAdmin || isUserSupport;
    $: isUserEnableSelectDoctor = isUserEnableSelectGroup || isUserManager;

    let preOpInstruction = {},
        isCreatingPreOpInstruction = false;

    let preparingListOptions = false,
        doctorsUnderGroup = [];

    let selectedGroup = {},
        selectedDoctor = {};

    onMount(async () => {
        await initListOptions();
    });

    const goToPreOpInstructionsList = () => goToURL(`/PreOpInstructions`);

    const initListOptions = async () => {
        preparingListOptions = true;

        if (isUserEnableSelectGroup) await cacheAllGroups();

        if (isUserEnableSelectDoctor && !doctorsUnderGroup?.length) {
            await cacheAccountPrePopulateSearchOptions();
            doctorsUnderGroup = $prePopulateAccountOptions;
        }

        preparingListOptions = false;
    };

    const createPreOpInstruction = async () => {
        _mapPreOpInstructionData();

        isCreatingPreOpInstruction = true;

        await apiMethods.postMethod
            .createPreOpInstruction(preOpInstruction)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to create PreOp Instruction, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    await cachePreOpInstructions(true);
                    goToURL(`/PreOpInstructions/view/${res.id}`);
                }
            });

        isCreatingPreOpInstruction = false;
    };

    const clearSelectedGroup = () => (selectedGroup = {});

    const clearSelectedDoctor = () => (selectedDoctor = {});

    // HELPER FUNCTIONS
    const _checkSubmitBtnDisabled = (data = {}) => {
        if (!data || isObjectEmpty(data)) return true;

        return !data.name;
    };

    const _mapPreOpInstructionData = () => {
        if (isUserManager || isUserDoctor)
            preOpInstruction.groupId = $user.groupId ?? Number(0);

        if (isUserDoctor) {
            preOpInstruction.groupId = $user.groupId ?? Number(0);
            preOpInstruction.accountId = $loggedInDoctorDetail.id ?? Number(0);
        } else if (isUserManager) {
            preOpInstruction.groupId = $user.groupId ?? Number(0);
            if (!selectedDoctor || !selectedDoctor.data)
                delete preOpInstruction.accountId;
            else preOpInstruction.accountId = selectedDoctor.data.id;
        } else {
            if (!selectedGroup || !selectedGroup.data)
                delete preOpInstruction.groupId;
            else preOpInstruction.groupId = selectedGroup.data.id;

            if (!selectedDoctor || !selectedDoctor.data)
                delete preOpInstruction.accountId;
            else preOpInstruction.accountId = selectedDoctor.data.id;
        }
    };
</script>

<PageHead
    title="Create Pre-Op Instruction"
    backFunc={goToPreOpInstructionsList}
/>

<CardLayout title="Instruction Details">
    <div class="flex flex-1 flex-col gap-y-2 w-full">
        <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
            <div class="flex flex-col flex-1 gap-y-4 h-auto">
                <div class="flex gap-x-4 items-center align-center">
                    <label for="name" class="shortTitle"> Name </label>

                    <input
                        type="text"
                        class="input input-bordered input-sm flex flex-1"
                        id="name"
                        bind:value={preOpInstruction.name}
                    />
                </div>

                {#if isUserEnableSelectGroup}
                    <div class="flex gap-x-4 items-center align-center">
                        <label for="name" class="shortTitle"> Group </label>

                        {#if preparingListOptions}
                            <div class="flex flex-1 gap-x-2 items-center">
                                <Loader />
                                <p>Loading groups</p>
                            </div>
                        {:else}
                            <Select
                                bind:value={selectedGroup.data}
                                class="flex flex-1 rounded-full"
                                items={$allGroups}
                                itemId="id"
                                label="name"
                                on:clear={clearSelectedGroup}
                                on:select={clearSelectedDoctor}
                                noOptionsMessage="No groups found"
                                --list-z-index="3"
                                --height="32px"
                            />
                        {/if}
                    </div>
                {/if}

                {#if isUserEnableSelectDoctor}
                    <div class="flex gap-x-4 items-center align-center">
                        <label for="name" class="shortTitle"> Doctor </label>

                        {#if preparingListOptions}
                            <div class="flex flex-1 gap-x-2 items-center">
                                <Loader />
                                <p>Loading doctors</p>
                            </div>
                        {:else}
                            <Select
                                bind:value={selectedDoctor.data}
                                class="flex flex-1 rounded-full"
                                items={doctorsUnderGroup}
                                itemId="id"
                                label="fullName"
                                on:clear={clearSelectedDoctor}
                                on:select={clearSelectedGroup}
                                noOptionsMessage="No doctors found"
                                --list-z-index="3"
                                --height="32px"
                            />
                        {/if}
                    </div>
                {/if}
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-auto">
                <div class="flex gap-x-4 items-center align-center">
                    <label
                        for="attachToEstimateEmail"
                        class="title cursor-pointer"
                    >
                        Attach to Estimate Email
                    </label>

                    <input
                        type="checkbox"
                        id="attachToEstimateEmail"
                        class="toggle toggle-secondary"
                        bind:checked={preOpInstruction.attachToEstimateEmail}
                    />
                </div>

                <div class="flex gap-x-4 items-center align-center">
                    <label for="addToEstimatePdf" class="title cursor-pointer">
                        Add To Estimate PDF
                    </label>

                    <input
                        type="checkbox"
                        id="addToEstimatePdf"
                        class="toggle toggle-secondary"
                        bind:checked={preOpInstruction.addToEstimatePdf}
                    />
                </div>

                <div class="flex gap-x-4 items-center align-center">
                    <label for="addToPortal" class="title cursor-pointer">
                        Add to Portal
                    </label>

                    <input
                        type="checkbox"
                        id="addToPortal"
                        class="toggle toggle-secondary"
                        bind:checked={preOpInstruction.addToPortal}
                    />
                </div>
            </div>
        </div>

        <div class="flex flex-col flex-1 gap-y-2 w-full px-2 py-4">
            <p>Content</p>

            <RichTextEditor
                bind:content={preOpInstruction.content}
                config={{
                    style: {
                        minHeight: "220px",
                    },
                }}
            />
        </div>
    </div>

    <div class="flex flex-1 items-center justify-end p-4 gap-4">
        <Button clickFunc={goBack} manualClass="w-[64px] btn-error">
            Cancel
        </Button>

        <Button
            clickFunc={createPreOpInstruction}
            manualClass={submitBtnDisabled
                ? "w-[64px] text-primary"
                : "w-[64px]"}
            isDisabled={submitBtnDisabled}
        >
            Create
        </Button>
    </div>
</CardLayout>

<style>
    .shortTitle {
        width: 80px;
    }

    .title {
        width: 200px;
    }
</style>
